import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import RestoreIcon from "@material-ui/icons/Restore";
import { useNotify, useRecordContext } from "react-admin";
import { hostname } from "../../providers/api";
import { useTokenOps } from "../../components/TokenContext";
import { triggerBlobDownload } from "../corporateCarbon/downloadFilename";
import { CarbonTechnicalDetails } from "../corporateCarbon/CarbonAdminPage";
import { reportingRevisionLabel, reportingRevisionStatusLabel } from "../corporateCarbon/workflow";
import "./AccountCarbonUpload.css";

const importErrors = {
  CARBON_IMPORT_PREVIEW_STALE: "This preview is out of date. Preview the workbook again before applying it.",
  CARBON_IMPORT_PREVIEW_REQUIRED: "Preview the workbook before applying it.",
  CARBON_IMPORT_TARGET_NOT_DRAFT: "The selected reporting revision is no longer a draft. Refresh and preview again.",
  CARBON_IMPORT_DRAFT_SELECTION_REQUIRED: "Confirm the suggested draft revision before applying this workbook.",
  CARBON_CORRECTION_REASON_REQUIRED: "Enter a reason for the correction and preview again.",
  CARBON_IMPORT_PERIOD_MISMATCH: "The workbook reporting period differs from the selected revision. Check the dates and upload the corrected workbook.",
  CARBON_IMPORT_REPORTING_PERIOD_MISMATCH: "The workbook reporting period differs from the selected revision. Check the dates and upload the corrected workbook.",
  CARBON_IMPORT_ACTIVITY_IDENTITY_AMBIGUOUS: "An activity cannot be matched unambiguously. Review the site and source values in the workbook.",
  CARBON_IMPORT_SOURCE_CATEGORY_CONFLICT: "An existing emission source has a different category. Correct the source category or name in the workbook.",
  CARBON_IMPORT_SOURCE_CONFIGURATION_CONFLICT: "An existing emission source uses different fuel or equipment settings. Use its original settings or give the new source a distinct name.",
  CARBON_IMPORT_DRAFT_SETTINGS_MISMATCH: "The workbook Organization settings differ from this draft. Align the reporting settings in Inventories or correct the Organization sheet, then preview again.",
};
const safeIssueText = (value) => typeof value === "string" && value.length <= 500 && !/^[A-Z][A-Z0-9_:-]+$|\b(?:QueryFailedError|Internal Server Error|stack trace|node_modules)\b|\n\s*at\s+/i.test(value) ? value : null;
const formatIssue = (issue, row) => {
  if (typeof issue === "string") return importErrors[issue] || safeIssueText(issue) || "Review this workbook row.";
  if (!issue || typeof issue !== "object") return "Review this workbook row.";
  const location = [issue.sheet || row?.sheetName, (issue.row || row?.rowNumber) && `row ${issue.row || row.rowNumber}`, issue.column && `column ${issue.column}`, issue.field && `field ${issue.field}`].filter(Boolean).join(" · ");
  const value = issue.value == null ? "" : ` · value: ${String(issue.value).slice(0, 120)}`;
  const message = safeIssueText(issue.message) || importErrors[issue.causeCode] || "Review this workbook value.";
  const resolution = safeIssueText(issue.resolution) || safeIssueText(issue.remediation);
  return `${location ? `${location}: ` : ""}${message}${value}${resolution ? ` · ${resolution}` : ""}`;
};
const requestError = async (response) => {
  const body = await response.json().catch(() => ({}));
  const code = body.code || (typeof body.message === "string" ? body.message : "");
  const issue = Array.isArray(body.issues) && body.issues.length ? ` ${body.issues.map((item) => formatIssue(item)).join("; ")}` : "";
  throw new Error(`${importErrors[code] || safeIssueText(body.message) || "The Carbon request could not be completed. Please retry or contact support."}${issue}`);
};

const AccountCarbonUpload = ({ accountId: accountIdProp }) => {
  const record = useRecordContext();
  const accountId = accountIdProp || record?.id;
  const tokenOps = useTokenOps();
  const notify = useNotify();
  const [file, setFile] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [batch, setBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [applyMode, setApplyMode] = useState("draft");
  const [preview, setPreview] = useState(null);
  const [removeMissingActivities, setRemoveMissingActivities] = useState(false);
  const [recalculationReason, setRecalculationReason] = useState("");
  const [confirmDraft, setConfirmDraft] = useState(false);
  const previewGeneration = useRef(0);
  const previewBusyRef = useRef(null);
  const batchGeneration = useRef(0);
  const historyGeneration = useRef(0);
  const busyGeneration = useRef(0);
  const accountGeneration = useRef(0);
  const accountContext = useRef(null);
  const [busy, setBusy] = useState(false);
  const token = tokenOps.account?.accessToken;
  const selectedAccountId = tokenOps.account?.data?.accountId;
  const selectedAccountName = tokenOps.account?.data?.accountName;
  const accountSelected = Boolean(accountId && accountId === selectedAccountId);
  const headers = { authorization: `Bearer ${token}` };
  if (accountContext.current?.token !== token || accountContext.current?.selectedAccountId !== selectedAccountId || accountContext.current?.accountId !== accountId) {
    accountContext.current = { token, selectedAccountId, accountId };
    accountGeneration.current += 1;
    batchGeneration.current += 1;
    historyGeneration.current += 1;
    previewGeneration.current += 1;
    busyGeneration.current += 1;
    previewBusyRef.current = null;
  }
  const currentAccount = (generation) => generation === accountGeneration.current && accountSelected;
  const currentBatch = (account, generation) => currentAccount(account) && generation === batchGeneration.current;
  const startBusy = () => { const generation = ++busyGeneration.current; setBusy(true); return generation; };
  const endBusy = (generation) => { if (generation === busyGeneration.current) setBusy(false); };
  const clearPreview = () => {
    previewGeneration.current += 1;
    setPreview(null);
    setConfirmDraft(false);
    if (previewBusyRef.current === busyGeneration.current) {
      previewBusyRef.current = null;
      busyGeneration.current += 1;
      setBusy(false);
    }
  };

  const loadHistory = useCallback(async () => {
    if (!accountSelected || !token) return;
    const account = accountGeneration.current;
    const generation = ++historyGeneration.current;
    const response = await fetch(`${hostname}/corporate-carbon/imports?page=1&limit=50`, { headers });
    if (!response.ok) return requestError(response);
    const payload = await response.json();
    if (currentAccount(account) && generation === historyGeneration.current) setHistory(payload.data || []);
  }, [accountSelected, token, accountId, selectedAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const account = accountGeneration.current;
    loadHistory().catch((error) => { if (account === accountGeneration.current && accountSelected) notify(error.message, "warning"); });
  }, [loadHistory, notify, accountSelected]);

  useEffect(() => {
    clearPreview();
    setBatch(null);
    setFile(null);
    setEvidenceFiles([]);
    setHistory([]);
    setBusy(false);
    setApplyMode("draft");
    setRemoveMissingActivities(false);
    setRecalculationReason("");
  }, [token, selectedAccountId, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBatch = async (id) => {
    if (!accountSelected) return false;
    const account = accountGeneration.current;
    const generation = ++batchGeneration.current;
    clearPreview();
    setBatch(null);
    const operation = startBusy();
    try {
      const response = await fetch(`${hostname}/corporate-carbon/imports/${id}`, { headers });
      if (!currentBatch(account, generation)) return false;
      if (!response.ok) await requestError(response);
      const value = await response.json();
      if (!currentBatch(account, generation)) return false;
      setBatch(value);
      return true;
    } catch (error) {
      if (currentBatch(account, generation)) notify(error.message, "warning");
      return false;
    } finally {
      endBusy(operation);
    }
  };

  const stage = async () => {
    if (!file || !accountSelected) return;
    const account = accountGeneration.current;
    const generation = ++batchGeneration.current;
    clearPreview();
    setBatch(null);
    const operation = startBusy();
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("schemaVersion", "v1");
      const response = await fetch(`${hostname}/corporate-carbon/imports`, { method: "POST", headers, body });
      if (!currentBatch(account, generation)) return;
      if (!response.ok) await requestError(response);
      const staged = await response.json();
      if (!currentBatch(account, generation)) return;
      setApplyMode("draft");
      setEvidenceFiles([]);
      setRemoveMissingActivities(false);
      setRecalculationReason("");
      const loaded = await loadBatch(staged.id);
      if (loaded && currentAccount(account)) {
        notify("Workbook checked and ready for review", "success");
        await loadHistory();
      }
    } catch (error) {
      if (currentBatch(account, generation)) notify(error.message, "warning");
    } finally {
      endBusy(operation);
    }
  };

  const previewChanges = async () => {
    if (!batch || batch.status !== "staged" || !accountSelected) return;
    const account = accountGeneration.current;
    const selectedBatch = batchGeneration.current;
    const current = ++previewGeneration.current;
    setPreview(null);
    setConfirmDraft(false);
    const operation = startBusy();
    previewBusyRef.current = operation;
    try {
      const response = await fetch(`${hostname}/corporate-carbon/imports/${batch.id}/preview`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ removeMissingActivities, ...(recalculationReason.trim() ? { recalculationReason: recalculationReason.trim() } : {}) }),
      });
      if (!currentBatch(account, selectedBatch) || current !== previewGeneration.current) return;
      if (!response.ok) await requestError(response);
      const value = await response.json();
      if (currentBatch(account, selectedBatch) && current === previewGeneration.current) setPreview(value);
    } catch (error) {
      if (currentBatch(account, selectedBatch) && current === previewGeneration.current) notify(error.message, "warning");
    } finally {
      if (previewBusyRef.current === operation) previewBusyRef.current = null;
      endBusy(operation);
    }
  };

  const changeStatus = async (action) => {
    if (!batch || !accountSelected) return;
    if (action === "apply" && (!preview?.previewToken || (preview.destination === "existing_draft" && !confirmDraft) || (preview.requiresReason && !recalculationReason.trim()))) return;
    const account = accountGeneration.current;
    const selectedBatch = batchGeneration.current;
    const operation = startBusy();
    try {
      const response = await fetch(`${hostname}/corporate-carbon/imports/${batch.id}/${action}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: action === "apply" ? JSON.stringify({ policy: "all_or_nothing", actorConfirmed: true, applyMode, previewToken: preview.previewToken, removeMissingActivities, ...(preview.destination === "existing_draft" ? { targetRevisionId: preview.targetRevisionId } : {}), ...(preview.requiresReason ? { recalculationReason: recalculationReason.trim() } : {}) }) : undefined,
      });
      if (!currentBatch(account, selectedBatch)) return;
      if (!response.ok) await requestError(response);
      const value = await response.json();
      if (!currentBatch(account, selectedBatch)) return;
      setBatch(value);
      clearPreview();
      notify(action === "apply" ? "Carbon workbook applied; the pre-apply backup is retained" : "Staged workbook discarded", "success");
      await loadHistory();
    } catch (error) {
      if (currentBatch(account, selectedBatch)) {
        if (action === "apply") clearPreview();
        notify(error.message, "warning");
      }
    } finally {
      endBusy(operation);
    }
  };

  const uploadEvidence = async () => {
    if (!batch || !evidenceFiles.length || !accountSelected) return;
    const zipFiles = evidenceFiles.filter((evidenceFile) => evidenceFile.name.toLowerCase().endsWith(".zip"));
    if (zipFiles.length && evidenceFiles.length !== 1) {
      notify("Upload either one ZIP archive or individual evidence files, not both.", "warning");
      return;
    }
    const account = accountGeneration.current;
    const selectedBatch = batchGeneration.current;
    const operation = startBusy();
    try {
      const body = new FormData();
      const archive = zipFiles.length === 1;
      evidenceFiles.forEach((evidenceFile) => body.append(archive ? "archive" : "files", evidenceFile));
      const response = await fetch(`${hostname}/corporate-carbon/imports/${batch.id}/evidence`, {
        method: "POST",
        headers,
        body,
      });
      if (!currentBatch(account, selectedBatch)) return;
      if (!response.ok) await requestError(response);
      const result = await response.json();
      if (!currentBatch(account, selectedBatch)) return;
      setBatch((current) => current?.id === batch.id ? { ...current, ...result } : current);
      setEvidenceFiles([]);
      if (result.failedCount) {
        const failed = (result.data || []).filter((item) => !item.success).map((item) => `${item.filename}: ${item.error?.message || "Upload failed"}`).join("; ");
        notify(`${result.uploadedCount} of ${result.total} evidence files accepted. ${failed}`, "warning");
      } else notify("Evidence uploaded and queued for security scanning", "success");
    } catch (error) {
      if (currentBatch(account, selectedBatch)) notify(error.message, "warning");
    } finally {
      endBusy(operation);
    }
  };

  const openDownload = async (item) => {
    if (!accountSelected) return;
    const account = accountGeneration.current;
    try {
      const response = await fetch(`${hostname}/corporate-carbon/imports/${item.id}/download`, { headers });
      if (!currentAccount(account)) return;
      if (!response.ok) await requestError(response);
      const filename = item.filename || "carbon-workbook.xlsx";
      const blob = await response.blob();
      if (!currentAccount(account)) return;
      triggerBlobDownload(blob, response, {
        displayFilename: filename,
        extension: filename.toLowerCase().endsWith(".xlsx") ? "" : ".xlsx",
      });
    } catch (error) {
      if (currentAccount(account)) notify(error.message, "warning");
    }
  };

  const recover = async (item) => {
    if (!accountSelected || !window.confirm("Create a new draft revision from this pre-apply backup?")) return;
    const account = accountGeneration.current;
    const operation = startBusy();
    try {
      const response = await fetch(`${hostname}/corporate-carbon/imports/${item.id}/recover`, { method: "POST", headers });
      if (!currentAccount(account)) return;
      if (!response.ok) await requestError(response);
      const revision = await response.json();
      if (currentAccount(account)) notify(`${reportingRevisionLabel(revision)} recovery draft created`, "success");
    } catch (error) {
      if (currentAccount(account)) notify(error.message, "warning");
    } finally {
      endBusy(operation);
    }
  };

  return (
    <div className="account-carbon-upload">
      <h2 className="account-carbon-upload__page-title">Carbon upload</h2>
      <section className="account-carbon-upload__context">
        <h3 className="account-carbon-upload__section-title">Context</h3>
        <div className="account-carbon-upload__context-fields">
          <div className="account-carbon-upload__context-field"><strong>Account</strong><span>{selectedAccountName || accountId || "—"}</span></div>
        </div>
        <CarbonTechnicalDetails><span>Account ID: {accountId || "—"}</span></CarbonTechnicalDetails>
      </section>
      <div className="account-carbon-upload__divider" />

      {!accountSelected ? (
        <div className="account-carbon-upload__notice">Switch into this account before staging or reviewing its Carbon workbooks. This prevents an upload from being assigned to the wrong account.</div>
      ) : (
        <>
          <section className="account-carbon-upload__section">
            <h3 className="account-carbon-upload__section-title">Raw data upload</h3>
            <p className="account-carbon-upload__description">Choose the completed Scope 1 and Scope 2 Excel template. We will check every row and then show a checklist of any supporting evidence filenames referenced by the workbook before anything is added to the account. A backup is created before approved data is applied.</p>
            <div className="account-carbon-upload__upload-area">
              <input id="carbon-workbook" aria-label="Carbon workbook" accept=".xlsx" type="file" className="account-carbon-upload__file-input" onChange={(event) => setFile(event.target.files[0] || null)} />
              {file && <div className="account-carbon-upload__file-preview"><img src="/preview-xlsx.png" alt="Excel file" /><span>{file.name}</span></div>}
            </div>
            <div className="account-carbon-upload__controls">
              <Button className="account-carbon-upload__primary-button" color="primary" variant="contained" disabled={!file || busy} onClick={stage}>{busy ? <CircularProgress size={20} /> : "Check workbook"}</Button>
            </div>
          </section>

          {batch && (
            <section className="account-carbon-upload__section">
              <div className="account-carbon-upload__section-header">
                <div><h3 className="account-carbon-upload__section-title">Review checked data</h3><p className="account-carbon-upload__description">{batch.filename} · {batch.acceptedRows || 0} ready · {batch.rejectedRows || 0} need attention</p></div>
                <Chip label={batch.status} color={batch.rejectedRows ? "secondary" : "primary"} />
              </div>
              <div className="account-carbon-upload__table-wrap">
                <Table size="small" aria-label="Staged Carbon workbook rows">
                  <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Record</TableCell><TableCell>Source</TableCell><TableCell>Validation</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(batch.rows || []).map((row) => <TableRow key={row.id || `${row.sheetName}-${row.rowNumber}`}><TableCell>{row.entityKind}</TableCell><TableCell>{row.externalKey}</TableCell><TableCell>{row.sheetName} · row {row.rowNumber}</TableCell><TableCell>{row.errors?.length ? row.errors.map((issue) => formatIssue(issue, row)).join("; ") : "Ready"}{row.warnings?.length ? ` · ${row.warnings.map((issue) => formatIssue(issue, row)).join("; ")}` : ""}</TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </div>
              <div className="account-carbon-upload__evidence">
                <h3 className="account-carbon-upload__section-title">Evidence checklist</h3>
                {(batch.evidenceChecklist || []).length ? (
                  <>
                    <div className="account-carbon-upload__table-wrap">
                      <Table size="small" aria-label="Carbon import evidence checklist">
                        <TableHead><TableRow><TableCell>File</TableCell><TableCell>Referenced by</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                        <TableBody>
                          {(batch.evidenceChecklist || []).map((item) => (
                            <TableRow key={item.normalizedFilename || item.filename}>
                              <TableCell>{item.filename}</TableCell>
                              <TableCell>{(item.references || []).map((reference) => `${reference.sheetName} · row ${reference.rowNumber}`).join(", ")}</TableCell>
                              <TableCell><Chip size="small" label={item.status} color={item.status === "uploaded" ? "primary" : item.status === "failed" ? "secondary" : "default"} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <p className="account-carbon-upload__description">Upload the exact filenames listed above. You can select several files or one ZIP archive.</p>
                    <input
                      aria-label="Evidence files or ZIP"
                      accept=".pdf,.csv,.xlsx,.png,.jpg,.jpeg,.zip"
                      multiple
                      type="file"
                      className="account-carbon-upload__file-input"
                      onChange={(event) => setEvidenceFiles(Array.from(event.target.files || []))}
                    />
                    <Box display="flex" style={{ gap: 10 }}>
                      <Button disabled={!evidenceFiles.length || busy} onClick={uploadEvidence}>Upload evidence</Button>
                      <Button disabled={busy} onClick={() => loadBatch(batch.id)}>Refresh evidence status</Button>
                    </Box>
                  </>
                ) : (
                  <p className="account-carbon-upload__description">No supporting evidence files were referenced in this workbook.</p>
                )}
              </div>
              {batch.status === "staged" && (
                <Box mt={2}>
                  <TextField
                    fullWidth
                    label="After importing"
                    margin="normal"
                    onChange={(event) => { clearPreview(); setApplyMode(event.target.value); }}
                    inputProps={{ "aria-label": "After importing" }}
                    SelectProps={{ native: true }}
                    select
                    value={applyMode}
                    variant="filled"
                  >
                    <option value="draft">Keep as draft for review</option>
                    <option value="finalize_after_validation">Finalize if all readiness checks pass</option>
                  </TextField>
                  <p className="account-carbon-upload__description">
                    {applyMode === "draft"
                      ? "The imported reporting revision remains editable until it is finalized explicitly."
                      : "The import and finalization are atomic: if any readiness check fails, no imported data is kept."}
                  </p>
                  <FormControlLabel control={<Checkbox checked={removeMissingActivities} onChange={(_, checked) => { clearPreview(); setRemoveMissingActivities(checked); }} />} label="Remove activities missing from workbook" />
                  <p className="account-carbon-upload__description">Missing activities are preserved by default. Selecting removal lists the affected activities in the preview.</p>
                  <TextField fullWidth label="Reason for correction" value={recalculationReason} onChange={(event) => { clearPreview(); setRecalculationReason(event.target.value); }} margin="normal" />
                  <Button disabled={busy} onClick={previewChanges}>Preview changes</Button>
                  {preview && <Box mt={2} aria-label="Import preview">
                    <p>{preview.reportingYear} · {preview.destination === "existing_draft" ? "Apply to existing draft" : preview.destination === "new_correction" ? "Create new correction" : "Create initial reporting data"} · {preview.additions} additions · {preview.updates} updates · {preview.removals} removals · {preview.preserved} preserved</p>
                    {preview.destination === "existing_draft" && <FormControlLabel control={<Checkbox checked={confirmDraft} onChange={(_, checked) => setConfirmDraft(checked)} />} label={`Apply to ${reportingRevisionStatusLabel({ ...preview, status: "draft" })}`} />}
                    {preview.requiresReason && !recalculationReason.trim() && <p role="alert">A reason for correction is required before applying.</p>}
                    {(preview.sites || []).length > 0 && <p>Sites: {preview.sites.map((site) => `${site.name || site.site} (${site.included ? "included" : "excluded"})`).join(", ")}</p>}
                    {(preview.changes || []).length > 0 && <ul>{preview.changes.map((change, index) => <li key={index}>{[change.sheet, change.row && `row ${change.row}`, change.site, change.source, change.action].filter(Boolean).join(" · ")}</li>)}</ul>}
                    {(preview.removedActivities || []).length > 0 && <><p>Activities to remove:</p><ul>{preview.removedActivities.map((item, index) => <li key={index}>{item.site} · {item.source} · {item.category}</li>)}</ul></>}
                  </Box>}
                  <Box display="flex" style={{ gap: 10 }}>
                    <Button className="account-carbon-upload__primary-button" color="primary" variant="contained" disabled={busy || !preview?.previewToken || (preview.destination === "existing_draft" && !confirmDraft) || (preview.requiresReason && !recalculationReason.trim()) || Boolean(batch.rejectedRows) || Boolean(batch.evidenceSummary && (batch.evidenceSummary.missing || batch.evidenceSummary.processing || batch.evidenceSummary.failed))} onClick={() => changeStatus("apply")}>Add data to account</Button>
                    <Button disabled={busy} onClick={() => changeStatus("discard")}>Discard</Button>
                  </Box>
                </Box>
              )}
            </section>
          )}

          <section className="account-carbon-upload__section">
            <h3 className="account-carbon-upload__section-title">Upload history</h3>
            <div className="account-carbon-upload__table-wrap">
              <Table size="small" aria-label="Carbon upload history">
                <TableHead><TableRow><TableCell>File</TableCell><TableCell>Status</TableCell><TableCell>Rows</TableCell><TableCell>Uploaded</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
                <TableBody>
                  {history.map((item) => <TableRow key={item.id} hover onClick={() => loadBatch(item.id)} style={{ cursor: "pointer" }}><TableCell>{item.filename}</TableCell><TableCell>{item.status}</TableCell><TableCell>{item.appliedRows || 0}/{item.totalRows || 0}</TableCell><TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}</TableCell><TableCell><Button size="small" startIcon={<GetAppIcon />} onClick={(event) => { event.stopPropagation(); openDownload(item); }}>Workbook</Button>{item.backupId && <Button size="small" disabled={busy} startIcon={<RestoreIcon />} onClick={(event) => { event.stopPropagation(); recover(item); }}>Recover</Button>}</TableCell></TableRow>)}
                  {!history.length && <TableRow><TableCell colSpan={5}>No Carbon uploads for this account.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AccountCarbonUpload;
